// src/hooks/macro/useEconomicCalendarEvents.ts

import { useQuery } from '@tanstack/react-query';
import { economicCalendarQueryOptions, type EconomicCalendarQueryParams } from '@/services/queries/macro/economicCalendarQueries';
import type { EconomicCalendarEvent } from '@/types/dashboards/macro';

export interface UseEconomicCalendarEventsOptions {
  startDate?: string; // YYYY-MM-DD format
  endDate?: string; // YYYY-MM-DD format
  enabled?: boolean;
  refetchInterval?: number;
}

export interface UseEconomicCalendarEventsResult {
  data: EconomicCalendarEvent[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  isSuccess: boolean;
  isError: boolean;
}

/**
 * React hook for fetching economic calendar events
 * @param options - Configuration options for the data fetch
 * @returns Object containing data, loading state, error, and refetch function
 */
export const useEconomicCalendarEvents = (options: UseEconomicCalendarEventsOptions = {}): UseEconomicCalendarEventsResult => {
  const { 
    startDate,
    endDate,
    enabled = true, 
    refetchInterval,
  } = options;

  const queryParams: EconomicCalendarQueryParams = {};
  if (startDate) queryParams.startDate = startDate;
  if (endDate) queryParams.endDate = endDate;

  const {
    data,
    isLoading,
    error,
    refetch,
    isSuccess,
    isError,
  } = useQuery({
    ...economicCalendarQueryOptions(queryParams),
    enabled,
    ...(refetchInterval !== undefined && { refetchInterval }),
  });

  return {
    data: data || [],
    isLoading,
    error: error as Error | null,
    refetch,
    isSuccess,
    isError,
  };
};
